import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../components/ui/common/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/common/popover";
import { useIngredientFilterState } from "../hooks/useIngredientFilterState";
import FilterSearchMultiSelect from "./FilterSearchMultiSelect";
import FilterSortOptions from "./FilterSortOptions";

interface IngredientFilterPanelProps {
  refetchWithFilters: (
    ingredientIds: string[],
    supplierIds: string[],
    dataTypeIds: string[],
    sortBy: number
  ) => void;
}

/**
 * Tailwind replacement for the legacy Semantic UI `IngredientFilter` popup
 * (see src/screens/ingredients/ingredientList/IngredientFilter.tsx) — same
 * three search-as-you-type multi-select dropdowns + sort options +
 * Reset/Apply, rebuilt on the common `Popover`/`Checkbox`/`RadioGroup`
 * primitives instead of `semantic-ui-react` + `react-select`.
 */
const IngredientFilterPanel = ({ refetchWithFilters }: IngredientFilterPanelProps) => {
  const [open, setOpen] = useState(false);
  const filterState = useIngredientFilterState(refetchWithFilters);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="tw-shrink-0 tw-gap-2">
          <SlidersHorizontal size={16} />
          Filters
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="tw-max-h-[70vh] tw-w-80 tw-overflow-y-auto">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-sm tw-font-semibold tw-text-foreground">Sort By</p>
          <FilterSearchMultiSelect
            label="Ingredient Name"
            placeholder="Search ingredient..."
            options={filterState.ingredientOptions}
            selectedValues={filterState.selectedIngredients}
            onToggle={filterState.toggleIngredient}
          />
          <FilterSearchMultiSelect
            label="Supplier Name"
            placeholder="Search supplier..."
            options={filterState.supplierOptions}
            selectedValues={filterState.selectedSuppliers}
            onToggle={filterState.toggleSupplier}
          />
          <FilterSearchMultiSelect
            label="Data Type"
            placeholder="Search data type..."
            options={filterState.dataTypeOptions}
            selectedValues={filterState.selectedDataTypes}
            onToggle={filterState.toggleDataType}
          />
          <FilterSortOptions sortBy={filterState.sortBy} onChange={filterState.setSortBy} />
          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-border tw-pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                filterState.reset();
                setOpen(false);
              }}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => {
                filterState.apply();
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IngredientFilterPanel;
